import { FC } from "react";
import { useParams } from "react-router-dom";
import useGetModel from "../hooks/getModel";
import BlogPost from "../models/blogPost";
import { Box, Divider, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScreenBase from "./ScreenBase";
import { getPublicBlogPostHeroImageUrl } from "../utils/fileUtils";

const BlogPostScreen: FC = () => {
	const theme = useTheme();
	const params = useParams<{ slug: string }>();
	const { data, loading } = useGetModel<BlogPost>('/blogPost/slug', params.slug);

	return (
		<ScreenBase
			scrollableChildren={(
				<Stack spacing={2}>
					{loading && (
						<Skeleton variant="text" width={500} height={50} />
					)}
					{!loading && data && (
						<>
							<Stack spacing={4} pt={4}>
								<Typography variant="h3" color="textPrimary" textAlign="center">
									{data.title}
								</Typography>
								<Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
									<Typography variant="body1" color="textSecondary">
										{data.author ? data.author : 'Tim Kulig'}
									</Typography>
									<FontAwesomeIcon icon="circle" style={{ color: theme.palette.text.secondary, fontSize: '0.5em' }} />
									<Typography variant="body1" color="textSecondary">
										{data.publishDate ? new Date(data.publishDate).toLocaleDateString('en-us', { dateStyle: 'long' }) : ''}
									</Typography>
								</Stack>
								{data.heroImageS3Key && (
									<Box
										sx={{
											width: '100%',
											height: 300,
											backgroundImage: `url(${getPublicBlogPostHeroImageUrl(data)})`,
											backgroundSize: 'cover',
											backgroundPosition: 'center',
											backgroundRepeat: 'no-repeat',
											borderRadius: 1
										}}
									/>
								)}
							</Stack>
							<Divider />
							<Box
								sx={{
									maxWidth: 700,
									typography: 'body1',
									color: theme.palette.text.secondary,
									'& h1': { color: theme.palette.text.primary },
									'& h2': { color: theme.palette.text.primary },
									'& h3': { color: theme.palette.text.primary },
									'& h4': { color: theme.palette.text.primary },
									'& h5': { color: theme.palette.text.primary },
									'& h6': { color: theme.palette.text.primary },
									'& span[data-text-color="red"]': { color: theme.palette.error.main },
									'& span[data-text-color="orange"]': { color: theme.palette.warning.main },
									'& span[data-text-color="yellow"]': { color: '#fff900' },
									'& span[data-text-color="green"]': { color: theme.palette.success.main },
									'& span[data-text-color="blue"]': { color: theme.palette.info.main },
									'& span[data-text-color="purple"]': { color: '#8000ff' },
									'& span[data-text-color="pink"]': { color: '#ff00ff' },
									'& span[data-text-color="gray"]': { color: theme.palette.text.disabled },
									'& figcaption': { fontSize: '0.8em' },
									'& a': { color: theme.palette.primary.main, letterSpacing: 'unset', transition: 'color 0.2s ease' },
									'& a:hover': { color: '#ff80a0' },
									'& [data-text-alignment="center"]': { textAlign: 'center' },
									'& [data-text-alignment="right"]': { textAlign: 'right' }
								}}
								dangerouslySetInnerHTML={{ __html: data.content }}
							/>
						</>
					)}
				</Stack>
			)}
		/>
	);
};

export default BlogPostScreen;
